import type React from 'react';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { KModal } from '@/ui-library/components/molecules/KModal';
import { KText, KImage } from '@/ui-library/components';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { InterviewDetailsAPI } from '@/components/shared/interview-scheduler/SchedulerDetails/api/InterviewDetailsAPI';
import { useShowToast } from '@/hooks/useShowToast';
import { useTzOptions } from '@/components/formBuilder/formFields/DateField/components/useTzOptions';
import { editMe } from '@/store/me/actions';

interface TimezoneOption {
  label: string;
  value: string;
  offset: string;
  abbreviation: string;
  fullName: string;
  shortName: string;
}

interface InterviewerWithoutTimezone {
  id: number;
  name: string;
  avatar?: string;
  email?: string;
}

interface SchInterviewerTimezoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewersWithoutTimezone: InterviewerWithoutTimezone[];
  onTimezoneUpdated: (
    _updatedInterviewers: { id: number; time_zone: string }[]
  ) => void;
}

export const SchInterviewerTimezoneModal: React.FC<
  SchInterviewerTimezoneModalProps
> = ({
  isOpen,
  onClose,
  interviewersWithoutTimezone = [],
  onTimezoneUpdated,
}) => {
  const toast = useShowToast();
  const dispatch = useDispatch();

  // Get timezone options from the shared hook (uses server data when available)
  const { timeZoneOptions, customFilterOption } = useTzOptions();

  // Fetch timezone data when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(editMe({}) as any);
    }
  }, [dispatch, isOpen]);

  // For single interviewer mode
  const [singleTimezone, setSingleTimezone] = useState<TimezoneOption | null>(
    null
  );

  // For multiple interviewers mode - store timezone for each interviewer
  const [interviewerTimezones, setInterviewerTimezones] = useState<
    Record<number, TimezoneOption | null>
  >({});

  // Safeguard: ensure array (memoized so hook dependency lists stay stable)
  const interviewers = useMemo(
    () =>
      Array.isArray(interviewersWithoutTimezone)
        ? interviewersWithoutTimezone
        : [],
    [interviewersWithoutTimezone]
  );

  const isSingleInterviewer = interviewers?.length === 1;
  const singleInterviewer = isSingleInterviewer ? interviewers?.[0] : null;

  const bulkUpdateMutation = useMutation({
    mutationFn: InterviewDetailsAPI?.updateTimezones,
  });

  const handleSingleTimezoneChange = useCallback(
    (selected: TimezoneOption | null) => {
      setSingleTimezone(selected);
    },
    []
  );

  const handleInterviewerTimezoneChange = useCallback(
    (interviewerId: number, selected: TimezoneOption | null) => {
      setInterviewerTimezones((prev) => ({
        ...prev,
        [interviewerId]: selected,
      }));
    },
    []
  );

  const handleSetTimezone = useCallback(async () => {
    try {
      // Build the users payload for bulk update
      const usersPayload: Record<string, string> = {};

      if (isSingleInterviewer && singleInterviewer && singleTimezone?.value) {
        // Single interviewer mode - use template literal for explicit string key
        usersPayload[`${singleInterviewer?.id}`] = singleTimezone?.value;
      } else {
        // Multiple interviewers mode - all must have timezone selected
        interviewers?.forEach((interviewer) => {
          const timezone = interviewerTimezones?.[interviewer?.id]?.value;
          if (timezone) {
            // Use template literal for explicit string key
            usersPayload[`${interviewer?.id}`] = timezone;
          }
        });
      }

      // Call bulk update API
      const result = await bulkUpdateMutation.mutateAsync(usersPayload);

      const bulkResult = result as { errors?: string[] };
      if ((bulkResult?.errors?.length ?? 0) > 0 || !result) {
        toast({
          title: bulkResult?.errors?.[0] || 'Failed to update timezone',
          status: 'error',
        });
        return;
      }
      toast({
        title: 'Timezone updated successfully',
        status: 'success',
      });

      // Notify parent of the updates
      const updatedInterviewers = Object.entries(usersPayload)?.map(
        ([id, timezone]) => ({
          id: Number(id),
          time_zone: timezone,
        })
      );

      onTimezoneUpdated(updatedInterviewers);
      onClose();
    } catch (error: any) {
      toast({
        title:
          error?.response?.data?.errors?.[0] ||
          error?.message ||
          'Failed to update timezone',
        status: 'error',
      });
    }
  }, [
    isSingleInterviewer,
    singleInterviewer,
    singleTimezone,
    interviewers,
    interviewerTimezones,
    // eslint-disable-next-line @tanstack/query/no-unstable-deps -- pre-existing; behavior preserved (extraction move)
    bulkUpdateMutation,
    onTimezoneUpdated,
    onClose,
    toast,
  ]);

  const handleClose = useCallback(() => {
    setSingleTimezone(null);
    setInterviewerTimezones({});
    onClose();
  }, [onClose]);

  // Check if save button should be disabled
  // Save is enabled only when ALL interviewers have their timezone selected
  const isSaveDisabled = useMemo(() => {
    if (isSingleInterviewer) {
      return !singleTimezone?.value;
    }
    // For multiple interviewers, ALL must have a timezone selected
    return !interviewers?.every(
      (interviewer) => interviewerTimezones?.[interviewer?.id]?.value
    );
  }, [isSingleInterviewer, singleTimezone, interviewers, interviewerTimezones]);

  return (
    <KModal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      ctaAction={handleSetTimezone}
      ctaLabel="Save"
      isCtaLoading={bulkUpdateMutation?.isPending}
      closeOnOverlayClick={false}
      hideCloseButton={false}
      enableCancelBtn={true}
      closeOnEsc={true}
      returnFocusOnClose={false}
      ctaButtonProps={{
        isDisabled: isSaveDisabled,
      }}
    >
      <Box>
        {isSingleInterviewer && singleInterviewer ? (
          // Single interviewer UI
          <>
            <KText size="md-medium" color="secondary-900" mb="4px">
              Set timezone for {singleInterviewer?.name}
            </KText>
            <KText size="sm" color="secondary-500" mb="16px">
              Set timezone so candidates can see their availability
            </KText>
            <Box mb="12px">
              <KText size="sm" color="secondary-700" mb="8px">
                Timezone
              </KText>
              <KSelect
                placeholder="Select timezone"
                value={singleTimezone}
                options={timeZoneOptions}
                onChange={handleSingleTimezoneChange}
                filterOption={customFilterOption}
                isSearchable
                menuPlacement="auto"
              />
            </Box>
          </>
        ) : (
          // Multiple interviewers UI
          <>
            <KText size="md-medium" color="secondary-900" mb="4px">
              Select timezones for the following interviewers
            </KText>
            <KText size="sm" color="secondary-500" mb="16px">
              Set timezones so candidates can see their availability
            </KText>
            <Flex flexDir="column" gap="16px" mb="12px">
              {interviewers?.map((interviewer) => (
                <Box key={interviewer?.id}>
                  <Flex alignItems="center" gap="8px" mb="8px">
                    <KText size="sm" color="secondary-900">
                      Timezone for
                    </KText>
                    <KImage
                      src={interviewer?.avatar}
                      name={interviewer?.name}
                      size="20px"
                    />
                    <KText size="sm-medium" color="secondary-900">
                      {interviewer?.name}
                    </KText>
                  </Flex>
                  <KSelect
                    placeholder="Select timezone"
                    value={interviewerTimezones?.[interviewer?.id] ?? null}
                    options={timeZoneOptions}
                    onChange={(selected) =>
                      handleInterviewerTimezoneChange(interviewer?.id, selected)
                    }
                    filterOption={customFilterOption}
                    isSearchable
                    menuPlacement="auto"
                  />
                </Box>
              ))}
            </Flex>
          </>
        )}
      </Box>
    </KModal>
  );
};
